import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Markdown } from 'tiptap-markdown'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Typography from '@tiptap/extension-typography'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { VaultImage } from './VaultImage'
import { EditorToolbar } from '@/components/editor/EditorToolbar'
import { getCardContent, saveCardContent } from '@/lib/api'
import { useVaultStore } from '@/stores/useVaultStore'
import './vault-editor.css'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'
const SAVE_DEBOUNCE_MS = 1200

/**
 * TipTap editor for a .md vault card. Markdown in / markdown out (tiptap-markdown
 * `storage.markdown.getMarkdown()`). Only the user body is sent to the server;
 * the region-preserving write endpoint keeps frontmatter + concierge fences
 * untouched. Rich ⇄ source toggle is the escape hatch for content the
 * round-trip would mangle.
 */
export function VaultCardEditor({ path }: { path: string }) {
  const [mode, setMode] = useState<'rich' | 'source'>('rich')
  const [source, setSource] = useState('')
  const [status, setStatus] = useState<SaveStatus>('idle')
  const isExternalRef = useRef(false)
  const pathRef = useRef(path)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setEditing = useVaultStore((s) => s.setEditing)
  const pane = useVaultStore((s) => s.pane)

  // Clear the global editing guard if we unmount mid-edit (navigating away),
  // so background auto-refresh doesn't stay paused vault-wide.
  useEffect(() => () => setEditing(false), [setEditing])

  const save = useCallback((body: string, targetPath: string) => {
    setStatus('saving')
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      try {
        await saveCardContent(targetPath, body)
        setStatus('saved')
      } catch {
        setStatus('error')
      }
    }, SAVE_DEBOUNCE_MS)
  }, [])

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
        breaks: true,
        linkify: true,
      }),
      Placeholder.configure({ placeholder: 'Write…' }),
      // Allow message:// (back to the source email in Mail.app — the whole
      // point of saving these) and obsidian:// through. Without this, TipTap's
      // link validator drops the href on load AND re-strips it on save, so
      // editing a card that contained a message link would silently destroy it.
      Link.configure({
        openOnClick: false,
        protocols: ['message', 'obsidian'],
        isAllowedUri: (url, ctx) =>
          /^(message|obsidian):/i.test(url) || ctx.defaultValidate(url),
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      VaultImage.configure({ allowBase64: true }),
    ],
    content: '',
    editorProps: { attributes: { class: 'vault-prose min-h-[40vh]' } },
    onFocus: () => setEditing(true),
    onBlur: () => setEditing(false),
    onUpdate: ({ editor }) => {
      if (isExternalRef.current) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const md = (editor.storage as any)?.markdown?.getMarkdown?.() ?? ''
      setSource(md)
      save(md, pathRef.current)
    },
    immediatelyRender: false,
  })

  // Load (or reload) content when the selected card changes.
  useEffect(() => {
    pathRef.current = path
    let cancelled = false
    setStatus('idle')
    getCardContent(path)
      .then((c) => {
        if (cancelled) return
        setSource(c.body)
        if (editor) {
          isExternalRef.current = true
          editor.commands.setContent(c.body, { emitUpdate: false })
          queueMicrotask(() => {
            isExternalRef.current = false
          })
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [path, editor])

  // Switching INTO rich mode: push the current source into the editor.
  useEffect(() => {
    if (mode === 'rich' && editor) {
      isExternalRef.current = true
      editor.commands.setContent(source, { emitUpdate: false })
      queueMicrotask(() => {
        isExternalRef.current = false
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const handleLinkClick = useCallback(() => {
    if (!editor) return
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-muted/20 px-2 py-1">
        {mode === 'rich' ? (
          <div className="min-w-0 flex-1">
            <EditorToolbar editor={editor} onLinkClick={handleLinkClick} noBorder active={pane === 'page'} />
          </div>
        ) : (
          <div className="flex-1 px-1 text-[11px] text-muted-foreground">raw markdown</div>
        )}
        <SaveDot status={status} />
        <button
          onClick={() => setMode(mode === 'rich' ? 'source' : 'rich')}
          className="rounded px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
        >
          {mode === 'rich' ? 'source' : 'rich'}
        </button>
      </div>

      <div className="px-3 py-3">
        {mode === 'rich' ? (
          <EditorContent editor={editor} />
        ) : (
          <textarea
            value={source}
            onChange={(e) => {
              setSource(e.target.value)
              save(e.target.value, pathRef.current)
            }}
            onFocus={() => setEditing(true)}
            onBlur={() => setEditing(false)}
            spellCheck={false}
            className="min-h-[40vh] w-full resize-y bg-transparent font-mono text-[13px] leading-relaxed text-foreground focus:outline-none"
          />
        )}
      </div>
    </div>
  )
}

function SaveDot({ status }: { status: SaveStatus }) {
  const map: Record<SaveStatus, { label: string; cls: string }> = {
    idle: { label: '', cls: 'text-muted-foreground' },
    saving: { label: 'saving…', cls: 'text-muted-foreground' },
    saved: { label: 'saved', cls: 'text-emerald-400' },
    error: { label: 'save failed', cls: 'text-red-400' },
  }
  const s = map[status]
  if (!s.label) return null
  return <span className={`text-[11px] ${s.cls}`}>{s.label}</span>
}
